import type { ReferenceIdentifier } from "@/lib/reference-identifiers";

export type MetadataFindingSeverity = "review" | "info";

export interface MetadataFinding {
  rule:
    | "year-conflict"
    | "title-conflict"
    | "metadata-incomplete"
    | "lookup-failed";
  field: "year" | "title" | "metadata";
  severity: MetadataFindingSeverity;
  message: string;
  written?: string;
  authoritative?: string;
}

const YEAR_REGEX = /\b(?:18|19|20|21)\d{2}[a-z]?\b/i;
const APA_DATE_REGEX = /\(((?:18|19|20|21)\d{2}[a-z]?|n\.d\.)\)/i;
const APA_TITLE_REGEX =
  /\((?:(?:18|19|20|21)\d{2}[a-z]?|n\.d\.)\)\.?\s*([^.!?]+[.!?])/i;
const YEAR_SUFFIX_REGEX = /[a-z]$/;
const WORD_REGEX = /[\p{L}\p{N}]+/gu;
const STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "for",
  "in",
  "of",
  "on",
  "the",
  "to",
  "with",
]);

function authoritativeYear(value: string | undefined): string | null {
  return value?.match(YEAR_REGEX)?.[0]?.slice(0, 4) ?? null;
}

function writtenDate(value: string): string | null {
  const date = APA_DATE_REGEX.exec(value)?.[1]?.toLowerCase();
  return date ? date.replace(YEAR_SUFFIX_REGEX, "").slice(0, 4) : null;
}

function writtenTitle(value: string): string | null {
  return APA_TITLE_REGEX.exec(value)?.[1]?.trim() ?? null;
}

function meaningfulTokens(value: string): Set<string> {
  const words = value.toLocaleLowerCase("en").match(WORD_REGEX) ?? [];
  return new Set(
    words.filter((word) => word.length > 2 && !STOP_WORDS.has(word))
  );
}

function titleOverlap(written: string, title: string): number {
  const titleTokens = meaningfulTokens(title);
  const writtenTokens = meaningfulTokens(written);
  let shared = 0;
  for (const token of titleTokens) {
    if (writtenTokens.has(token)) {
      shared += 1;
    }
  }
  return titleTokens.size === 0 ? 1 : shared / titleTokens.size;
}

/**
 * Compare only fields that can be extracted from formatted prose conservatively.
 * Findings indicate disagreement with retrieved metadata, never source validity.
 */
export function compareReferenceMetadata(
  writtenLine: string,
  authoritative: { title?: string; issued?: string }
): MetadataFinding[] {
  const findings: MetadataFinding[] = [];
  const writtenYear = writtenDate(writtenLine);
  const retrievedYear = authoritativeYear(authoritative.issued);
  if (writtenYear && retrievedYear && writtenYear !== retrievedYear) {
    findings.push({
      rule: "year-conflict",
      field: "year",
      severity: "review",
      message: `The written year (${writtenYear}) differs from the retrieved record (${retrievedYear}). Check the edition or version before changing it.`,
      written: writtenYear,
      authoritative: retrievedYear,
    });
  }

  const title = authoritative.title?.trim();
  const writtenTitleValue = writtenTitle(writtenLine);
  if (
    title &&
    writtenTitleValue &&
    titleOverlap(writtenTitleValue, title) < 0.6
  ) {
    findings.push({
      rule: "title-conflict",
      field: "title",
      severity: "review",
      message:
        "The title in this reference appears substantially different from the retrieved record. Confirm that the identifier belongs to the work you intended to cite.",
      authoritative: title,
    });
  }

  if (!(authoritative.title && authoritative.issued)) {
    findings.push({
      rule: "metadata-incomplete",
      field: "metadata",
      severity: "info",
      message:
        "The metadata provider returned an incomplete record. Review missing fields manually rather than treating this lookup as complete.",
    });
  }
  return findings;
}

export interface VerifiedMetadata {
  identifier: ReferenceIdentifier;
  entry: { title?: string; issued?: string } | null;
  findings: MetadataFinding[];
  error?: string;
}
